import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { injectIntl } from 'react-intl';
import classNames from 'classnames';
import get from 'lodash/get';

import { Modal } from 'sa-ui';
import { api } from '../../app/api';

import messages from './ComposingCorrectionUploadEssaySheetModalMessages';

import './ComposingCorrectionUploadEssaySheetModal.scss';

function chainPromises(arr) {
  // take an array and a function
  // invalid input
  if (!Array.isArray(arr)) return Promise.reject(new Error('Non array passed to each'));
  // empty case
  if (arr.length === 0) return Promise.resolve();
  return arr.reduce((prev, cur) => prev.then(() => cur()), Promise.resolve());
}

const Status = {
  PENDING: 0,
  UPLOADING: 1,
  UPLOADED: 2,
  ERROR: 3,
};

const ComposingCorrectionUploadEssaySheetModal = (props) => {
  const inputFileRef = useRef();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);

  function getUploadErrorMessage(error) {
    switch (true) {
      case /Invalid extension - /g.test(error):
        return messages.invalidExtension;
      case /Integration data not found/g.test(error):
        return messages.errorIntergrationDataNotFound;
      case /Permission denied/g.test(error):
        return messages.errorNoPermission;
      case /Error while uploading the file to the server/g.test(error):
        return messages.errorUploadingFile;
      case /Error while integrating file/g.test(error):
        return messages.errorIntegratingFile;
      case /Error while uploading the file. Try again uploading the file to the student/g.test(
        error,
      ):
        return messages.errorUploadingFileToStudent;
      default:
        return messages.errorGeneric;
    }
  }

  function uploadFile(file) {
    const formData = new FormData();
    formData.append('idEvaluation', props.idEvaluation);
    formData.append('idCSchool', props.idCSchool);
    formData.append('idCourse', props.idESCourse);
    formData.append('file', file);
    if (props.student) {
      formData.append('idUser', props.student.idUser);
    }
    props.onError(false);
    return api
      .post('/evaluation-system/uploadEvaluationEssayCard.php', formData)
      .then(() => Promise.resolve())
      .catch((err) => {
        console.error('erro no upload', err.response.data);
        const serverErrorMessage = get(err, 'response.data.error');
        const messageError = getUploadErrorMessage(serverErrorMessage);
        const messageIntl = props.intl.formatMessage(messageError);
        props.onError(messageError.defaultMessage);
        return Promise.resolve(messageIntl);
      });
  }

  function enqueueFiles(newFiles) {
    // console.log(props);

    const pendingFiles = newFiles.map((file, index) => ({
      file,
      id: index + files.length,
      status: Status.PENDING,
    }));
    const previousFiles = files.some((f) => f.status === Status.ERROR) ? [] : files;
    if (props.singularFile) {
      setFiles(pendingFiles);
    } else {
      setFiles(previousFiles.concat(pendingFiles));
    }

    console.log('uploading files...', pendingFiles, previousFiles.concat(pendingFiles));
  }

  async function sendFiles() {
    const pendingFiles = files.filter((c) => c.status === Status.PENDING);
    let currentFiles = files.map((f) => {
      if (f.status === Status.PENDING) {
        return {
          ...f,
          status: Status.UPLOADING,
          message: props.intl.formatMessage(messages.uploading),
        };
      }
      return f;
    });
    setFiles(currentFiles);
    setLoading(true);
    const promisesFiles = pendingFiles.map(
      (fileContainer) => () =>
        uploadFile(fileContainer.file).then((result) => {
          currentFiles = currentFiles.map((f) => {
            if (f.id === fileContainer.id) {
              return {
                ...f,
                status: result ? Status.ERROR : Status.UPLOADED,
                message: result || props.intl.formatMessage(messages.uploadedSuccess),
              };
            }
            return f;
          });
          console.log('filesUpdated', files, currentFiles);
          setFiles(currentFiles);
        }),
    );
    try {
      await chainPromises(promisesFiles);
    } finally {
      setLoading(false);
      if (props.onCompleteUpload) props.onCompleteUpload(files);
    }
  }

  useEffect(() => {
    setFiles([]);
    setLoading(false);
  }, [props.show]);

  const onDrop = useCallback((acceptedFiles) => {
    enqueueFiles(acceptedFiles);
  }, []);

  function handleUpload(e) {
    const acceptedFiles = Array.from(e.target.files);
    enqueueFiles(acceptedFiles);
  }

  function handleTriggerUpload() {
    if (loading) return;
    inputFileRef.current.click();
  }

  const { getRootProps } = useDropzone({ onDrop });

  return (
    <Modal
      title={
        props.singularFile
          ? props.intl.formatMessage(messages.singularTitle)
          : props.intl.formatMessage(messages.pluralTitle)
      }
      show={props.show}
      onClose={props.onClose}
      buttons={[
        {
          label: props.intl.formatMessage(messages.cancel),
          onClick: props.onClose,
        },
        {
          label: loading
            ? props.intl.formatMessage(messages.loading)
            : props.intl.formatMessage(messages.continue),
          onClick: files.every((file) => file.status === Status.UPLOADED)
            ? props.onClose
            : sendFiles,
          primary: true,
          disabled: loading || files.every((file) => file.status === Status.UPLOADING),
        },
      ]}
    >
      <div className="upload-answer-modal">
        <div {...getRootProps()} onClick={handleTriggerUpload} className="upload-border">
          <br />
          <i className="icon icon-small ti-cloud-up" />
          <h6 className="subtitle subtitle-bigger mt-2">
            {props.intl.formatMessage(messages.dragDrop)}
            <br />
            <span>
              <input
                ref={inputFileRef}
                type="file"
                id="file"
                name="file"
                accept=".pdf, image/jpeg, image/png"
                style={{ display: 'none' }}
                onChange={handleUpload}
                disabled={loading}
                multiple={!props.singularFile}
              />
              {props.intl.formatMessage(messages.clickHere)}
            </span>
          </h6>
        </div>
        {!props.singularFile && (
          <p style={{ color: '#969696' }} className="mb-0">
            {props.intl.formatMessage(messages.alreadyAddedComposing, { files: files.length })}
          </p>
        )}
        <div className="table-container">
          <table>
            <tbody>
              {files.map((fileContainer) => (
                <tr
                  className={classNames('text-left', {
                    'text-success': fileContainer.status === Status.UPLOADED,
                    'text-danger': fileContainer.status === Status.ERROR,
                  })}
                >
                  <td>
                    <i className="far fa-file-alt" />
                    {fileContainer.file.name}
                  </td>
                  <td>{fileContainer.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
};

export default injectIntl(ComposingCorrectionUploadEssaySheetModal);
